import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLangPath } from '../../hooks/useLangPath';

const SERVICES = [
  {
    icon: '📱',
    color: '#8258c8',
    title: 'Mobile App Development',
    tagline: 'Native & cross-platform apps for iOS and Android.',
    description: 'Native and cross-platform iOS & Android apps built for performance, smooth UX, and offline-first architecture.',
    features: ['Flutter & React Native', 'Native Swift / Kotlin', 'Push Notifications & BioAuth', 'App Store Deployment'],
    projects: ['Medical Platform', 'KIOSK Self-Ordering', 'Islamic App', 'Hakeem Oyoon', 'Duaa App'],
  },
  {
    icon: '🤖',
    color: '#45d0bd',
    title: 'AI & Machine Learning',
    tagline: 'Custom models, vision systems & LLM integrations.',
    description: 'Custom AI models, computer vision systems, NLP engines, and LLM integrations that solve real business problems.',
    features: ['Computer Vision & OCR', 'Speech Recognition', 'Predictive Models', 'Edge AI Deployment'],
    projects: ['FaceBot AI', 'Oman AI Quran', 'Sign Collector', 'Electrical Reader', 'Bulldozer Safety'],
  },
  {
    icon: '🌐',
    color: '#2c84c8',
    title: 'Web Platform Development',
    tagline: 'Scalable SaaS, dashboards & full-stack backends.',
    description: 'Scalable full-stack web applications, SaaS products, admin dashboards, and API backends for modern businesses.',
    features: ['Laravel & Vue.js / React', 'REST & GraphQL APIs', 'Multi-tenant SaaS', 'Admin Dashboards'],
    projects: ['Masjid Platform', 'Duaa Database', 'BlueYamama', 'Smart Ledger', 'East CR'],
  },
  {
    icon: '🏪',
    color: '#8258c8',
    title: 'Restaurant & POS Tech',
    tagline: 'Smart kiosks, self-ordering & kitchen display systems.',
    description: 'End-to-end restaurant digitization — from smart kiosks and self-ordering screens to kitchen display and POS systems.',
    features: ['Touch-screen Kiosks', 'POS Integration', 'Kitchen Display Systems', 'E-payment Gateways'],
    projects: ['KIOSK Self-Ordering', 'Smart Restaurant', 'Hungry Buten', 'DineOS'],
  },
  {
    icon: '🏥',
    color: '#45d0bd',
    title: 'HealthTech Solutions',
    tagline: 'Digital health platforms from appointments to AI diagnostics.',
    description: 'Digital health platforms for clinics, hospitals, and wellness providers — from appointment management to AI diagnostics.',
    features: ['Appointment Management', 'Patient Tracking', 'Medical Content Systems', 'Therapy Platforms'],
    projects: ['Medical Platform', 'Hakeem Oyoon', 'Autism Therapy Robot', 'VR Therapy', 'Iraq NovoCheck'],
  },
  {
    icon: '💳',
    color: '#2c84c8',
    title: 'FinTech & Business Tools',
    tagline: 'Invoice management, payments & financial analytics.',
    description: 'Smart financial platforms, invoice management, account tracking, and AI-driven business intelligence systems.',
    features: ['Invoice & Billing Systems', 'Payment Integrations', 'AI Financial Analytics', 'Multi-currency Support'],
    projects: ['Safqa App', 'Smart Ledger', 'BM Meubelen', 'MRZ Scanner SDK'],
  },
  {
    icon: '🦾',
    color: '#8258c8',
    title: 'Robotics & Embedded Systems',
    tagline: 'Intelligent robots, IoT & embedded firmware.',
    description: 'Intelligent robotic systems, IoT integrations, and embedded firmware for industrial and healthcare environments.',
    features: ['Embedded Firmware', 'Sensor Integration', 'MQTT / WebSocket Telemetry', 'Safety Systems'],
    projects: ['Autism Therapy Robot', 'Bulldozer Safety App', 'VR Therapy'],
  },
  {
    icon: '📦',
    color: '#45d0bd',
    title: 'SDK & Developer Tools',
    tagline: 'Custom Android SDKs, OCR libraries & API toolkits.',
    description: 'Custom Android SDKs, API libraries, and developer tools for integrating complex functionalities into any platform.',
    features: ['Android SDK Development', 'OCR & Image Processing', 'Barcode & MRZ Scanning', 'API Wrappers'],
    projects: ['MRZ Scanner SDK', 'SMS Gateway', 'App Cloner', 'Iraq NovoCheck'],
  },
];

const SERVICES_AR = [
  {
    icon: '📱',
    color: '#8258c8',
    title: 'تطوير تطبيقات الجوال',
    tagline: 'تطبيقات أصلية ومتعددة المنصات لـ iOS وAndroid.',
    description: 'تطبيقات iOS وAndroid أصلية ومتعددة المنصات مبنية للأداء العالي وتجربة مستخدم سلسة وبنية تعمل بدون اتصال.',
    features: ['Flutter وReact Native', 'Swift وKotlin الأصليان', 'الإشعارات والتوثيق البيومتري', 'النشر على متاجر التطبيقات'],
    projects: ['المنصة الطبية', 'كشك الطلب الذاتي', 'تطبيق إسلامي', 'حكيم عيون', 'تطبيق الدعاء'],
  },
  {
    icon: '🤖',
    color: '#45d0bd',
    title: 'الذكاء الاصطناعي وتعلم الآلة',
    tagline: 'نماذج مخصصة وأنظمة رؤية وتكاملات LLM.',
    description: 'نماذج ذكاء اصطناعي مخصصة وأنظمة رؤية حاسوبية ومحركات معالجة اللغة الطبيعية وتكاملات LLM تحل مشاكل الأعمال الحقيقية.',
    features: ['رؤية الحاسوب والتعرف الضوئي', 'التعرف على الكلام', 'النماذج التنبؤية', 'نشر الذكاء الاصطناعي الطرفي'],
    projects: ['FaceBot AI', 'قرآن AI عُمان', 'جامع الإشارات', 'قارئ العداد', 'سلامة الحفار'],
  },
  {
    icon: '🌐',
    color: '#2c84c8',
    title: 'تطوير المنصات الويب',
    tagline: 'تطبيقات SaaS ولوحات تحكم وخلفيات متكاملة.',
    description: 'تطبيقات ويب متكاملة قابلة للتوسع ومنتجات SaaS ولوحات تحكم وخلفيات API للأعمال الحديثة.',
    features: ['Laravel و Vue.js / React', 'REST وGraphQL APIs', 'SaaS متعدد المستأجرين', 'لوحات تحكم إدارية'],
    projects: ['منصة المسجد', 'قاعدة بيانات الدعاء', 'BlueYamama', 'دفتر الأستاذ الذكي', 'East CR'],
  },
  {
    icon: '🏪',
    color: '#8258c8',
    title: 'تقنية المطاعم ونقاط البيع',
    tagline: 'أكشاك ذكية وطلب ذاتي وأنظمة عرض المطبخ.',
    description: 'رقمنة شاملة للمطاعم — من الأكشاك الذكية وشاشات الطلب الذاتي إلى أنظمة عرض المطبخ ونقاط البيع.',
    features: ['أكشاك شاشة لمس', 'تكامل نقاط البيع', 'أنظمة عرض المطبخ', 'بوابات الدفع الإلكتروني'],
    projects: ['كشك الطلب الذاتي', 'المطعم الذكي', 'Hungry Buten', 'DineOS'],
  },
  {
    icon: '🏥',
    color: '#45d0bd',
    title: 'حلول التقنية الصحية',
    tagline: 'منصات صحة رقمية من المواعيد إلى التشخيص بالذكاء الاصطناعي.',
    description: 'منصات صحة رقمية للعيادات والمستشفيات ومزودي الرعاية — من إدارة المواعيد إلى التشخيص بالذكاء الاصطناعي.',
    features: ['إدارة المواعيد', 'تتبع المرضى', 'أنظمة المحتوى الطبي', 'منصات العلاج'],
    projects: ['المنصة الطبية', 'حكيم عيون', 'روبوت علاج التوحد', 'العلاج بالواقع الافتراضي', 'Iraq NovoCheck'],
  },
  {
    icon: '💳',
    color: '#2c84c8',
    title: 'التقنية المالية وأدوات الأعمال',
    tagline: 'إدارة الفواتير والمدفوعات والتحليلات المالية.',
    description: 'منصات مالية ذكية وإدارة فواتير وتتبع حسابات وأنظمة ذكاء أعمال مدفوعة بالذكاء الاصطناعي.',
    features: ['أنظمة الفوترة والفواتير', 'تكاملات الدفع', 'التحليلات المالية بالذكاء الاصطناعي', 'دعم متعدد العملات'],
    projects: ['تطبيق صفقة', 'دفتر الأستاذ الذكي', 'BM Meubelen', 'MRZ Scanner SDK'],
  },
  {
    icon: '🦾',
    color: '#8258c8',
    title: 'الروبوتات والأنظمة المدمجة',
    tagline: 'روبوتات ذكية وإنترنت الأشياء وبرمجيات مدمجة.',
    description: 'أنظمة روبوتية ذكية وتكاملات إنترنت الأشياء وبرمجيات مدمجة للبيئات الصناعية والرعاية الصحية.',
    features: ['البرمجيات المدمجة', 'تكامل أجهزة الاستشعار', 'بروتوكول MQTT / WebSocket', 'أنظمة السلامة'],
    projects: ['روبوت علاج التوحد', 'تطبيق سلامة الحفار', 'العلاج بالواقع الافتراضي'],
  },
  {
    icon: '📦',
    color: '#45d0bd',
    title: 'SDK وأدوات المطورين',
    tagline: 'SDKs أندرويد مخصصة ومكتبات OCR وحزم API.',
    description: 'SDKs أندرويد مخصصة ومكتبات API وأدوات مطورين لدمج الوظائف المعقدة في أي منصة.',
    features: ['تطوير Android SDK', 'معالجة الصور والتعرف الضوئي', 'مسح الباركود وMRZ', 'مغلفات API'],
    projects: ['MRZ Scanner SDK', 'بوابة SMS', 'App Cloner', 'Iraq NovoCheck'],
  },
];

const STATS = [
  { value: '10+', label: 'Industries Served' },
  { value: '8+',  label: 'Countries Reached' },
  { value: '100%', label: 'Client Satisfaction' },
];

const STATS_AR = [
  { value: '10+', label: 'قطاع مخدوم' },
  { value: '8+',  label: 'دولة وصلنا إليها' },
  { value: '100%', label: 'رضا العملاء' },
];

const glass = {
  background: 'rgba(10, 10, 18, 0.58)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
};

/* ── Modal overlay backdrop ── */
const Overlay = ({ onClick }) => (
  <div
    onClick={onClick}
    style={{
      position: 'fixed', inset: 0,
      background: 'rgba(5,5,7,0.82)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      zIndex: 1200,
    }}
  />
);

/* ── Service detail popup ── */
const ServiceModal = ({ svc, onClose }) => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  // Close on Escape
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <Overlay onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(640px, 92vw)',
          maxHeight: '85vh',
          overflowY: 'auto',
          zIndex: 1201,
          ...glass,
          borderRadius: 24,
          border: `1px solid ${svc.color}30`,
          boxShadow: `0 0 80px ${svc.color}20, 0 32px 80px rgba(0,0,0,0.6)`,
          padding: '36px',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '50%', width: 34, height: 34,
            color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
            fontSize: 16, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s',
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, fontSize: '1.7rem',
            background: `${svc.color}15`, border: `1px solid ${svc.color}35`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {svc.icon}
          </div>
          <div>
            <h2 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 800, margin: 0, marginBottom: 4 }}>
              {svc.title}
            </h2>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
              background: `${svc.color}15`, border: `1px solid ${svc.color}30`, color: svc.color,
            }}>
              {isAr ? `${svc.projects.length} مشروع` : `${svc.projects.length} projects`}
            </span>
          </div>
        </div>

        {/* Description */}
        <p style={{ color: '#aaaacc', lineHeight: 1.75, marginBottom: 28, fontSize: '0.95rem' }}>
          {svc.description}
        </p>

        {/* Features */}
        <div style={{
          background: `${svc.color}08`,
          border: `1px solid ${svc.color}18`,
          borderRadius: 14, padding: '20px 24px', marginBottom: 24,
        }}>
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
            {isAr ? 'القدرات الرئيسية' : 'Key Capabilities'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
            {svc.features.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  background: `${svc.color}18`, border: `1px solid ${svc.color}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={svc.color} strokeWidth="3">
                    <path d="M5 12l5 5 9-9"/>
                  </svg>
                </div>
                <span style={{ color: '#ccccdd', fontSize: '0.875rem' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Projects */}
        <div>
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
            {isAr ? 'المشاريع ذات الصلة' : 'Related Projects'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {svc.projects.map(p => (
              <span key={p} style={{
                fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20,
                background: `${svc.color}12`, border: `1px solid ${svc.color}28`, color: svc.color,
              }}>{p}</span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Compact service card ── */
const ServiceCard = ({ svc, index, onClick }) => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  return (
  <div
    className="col-sm-6 col-lg-4 col-xl-3"
    style={{ cursor: 'pointer' }}
    onClick={onClick}
  >
    <div
      style={{
        ...glass,
        padding: '26px 24px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
        borderColor: 'rgba(255,255,255,0.07)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = `0 20px 48px ${svc.color}22, 0 8px 24px rgba(0,0,0,0.3)`;
        e.currentTarget.style.borderColor = `${svc.color}35`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
      }}
    >
      {/* Top row: icon + index */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          width: 46, height: 46, borderRadius: 13, fontSize: '1.4rem',
          background: `${svc.color}15`, border: `1px solid ${svc.color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {svc.icon}
        </div>
        <span style={{ fontSize: '2rem', fontWeight: 900, color: `${svc.color}20`, lineHeight: 1 }}>
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      {/* Title */}
      <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0, lineHeight: 1.3 }}>
        {svc.title}
      </h3>

      {/* One-liner tagline */}
      <p style={{
        color: '#7878a0', fontSize: '0.82rem', lineHeight: 1.55,
        margin: 0, flex: 1,
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {svc.tagline}
      </p>

      {/* "View details" link */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        color: svc.color, fontSize: '0.8rem', fontWeight: 700, marginTop: 4,
      }}>
        <span>{isAr ? 'عرض التفاصيل' : 'View details'}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
      </div>
    </div>
  </div>
  );
};

const Services = () => {
  const { t, i18n } = useTranslation();
  const getPath = useLangPath();
  const isAr = i18n.language === 'ar';
  const services = isAr ? SERVICES_AR : SERVICES;
  const stats = isAr ? STATS_AR : STATS;
  const [active, setActive] = useState(null); // index of open modal

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = active !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [active]);

  return (
    <>
      {/* ── Hero ── */}
      <section style={{ padding: '170px 0 50px', textAlign: 'center', background: 'transparent' }}>
        <div className="container">
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(130,88,200,0.12)', border: '1px solid rgba(130,88,200,0.3)',
            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8258c8', display: 'inline-block' }} />
            <span style={{ color: '#8258c8', fontSize: '13px', fontWeight: 600 }}>{isAr ? '٨ مجالات خدمة أساسية' : '8 Core Service Areas'}</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2.2rem,5vw,3.6rem)', fontWeight: 800, lineHeight: 1.15, marginBottom: 18 }}>
            {isAr ? <>ماذا <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>نبني</span></> : <>What We{' '}<span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Build</span></>}
          </h1>
          <p style={{ color: '#6b6b88', maxWidth: '560px', margin: '0 auto', fontSize: '1rem', lineHeight: 1.7 }}>
            {isAr ? 'من تطبيقات الجوال المدعومة بالذكاء الاصطناعي إلى أكشاك المطاعم الذكية — انقر على أي بطاقة لاستكشاف قدراتنا الكاملة.' : 'From AI-powered mobile apps to smart restaurant kiosks — click any card to explore our full capabilities.'}
          </p>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <section style={{ background: 'transparent', padding: '0 0 56px' }}>
        <div className="container">
          <div className="row g-3">
            {stats.map(s => (
              <div className="col-6 col-md-3" key={s.label}>
                <div style={{ ...glass, padding: '20px', textAlign: 'center' }}>
                  <div style={{
                    fontSize: '1.9rem', fontWeight: 800,
                    background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                    {s.value}
                  </div>
                  <div style={{ color: '#6b6b88', fontSize: '0.82rem', marginTop: 4 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services Grid (compact cards) ── */}
      <section style={{ background: 'transparent', padding: '0 0 90px' }}>
        <div className="container">
          <div className="row g-4">
            {services.map((svc, i) => (
              <ServiceCard key={i} svc={svc} index={i} onClick={() => setActive(i)} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section style={{ background: 'transparent', padding: '0 0 90px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <h2 style={{ color: '#fff', fontSize: 'clamp(1.7rem,4vw,2.4rem)', fontWeight: 700, marginBottom: 10 }}>
              {isAr ? <>مجموعة <span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>التقنيات</span></> : <>Our{' '}<span style={{ background: 'linear-gradient(102deg, #8258c8, #2c84c8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Tech Stack</span></>}
            </h2>
            <p style={{ color: '#6b6b88', maxWidth: 460, margin: '0 auto', fontSize: '0.95rem' }}>
              {isAr ? 'تقنيات مُثبتة صناعياً اختيرت للأداء وقابلية التوسع وسهولة الصيانة.' : 'Industry-proven technologies chosen for performance, scalability, and maintainability.'}
            </p>
          </div>
          <div className="row g-3">
            {[
              { cat: 'Mobile',   techs: ['Flutter', 'React Native', 'Swift', 'Kotlin', 'Android'] },
              { cat: 'Backend',  techs: ['Laravel', 'Node.js', 'PHP', 'Python', 'REST API'] },
              { cat: 'Frontend', techs: ['Vue.js', 'React', 'HTML5', 'Tailwind CSS', 'TypeScript'] },
              { cat: 'AI & Data',techs: ['TensorFlow', 'OpenCV', 'Tesseract OCR', 'Firebase ML', 'Python AI'] },
              { cat: 'Cloud',    techs: ['Firebase', 'AWS', 'Google Cloud', 'Docker', 'CI/CD'] },
              { cat: 'Database', techs: ['MySQL', 'PostgreSQL', 'Firebase', 'MongoDB', 'Redis'] },
            ].map(group => (
              <div className="col-md-4 col-lg-2" key={group.cat}>
                <div style={{ ...glass, padding: '18px', height: '100%' }}>
                  <div style={{ color: '#8258c8', fontSize: '10px', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
                    {group.cat}
                  </div>
                  {group.techs.map(tech => (
                    <div key={tech} style={{ color: '#ccccdd', fontSize: '12px', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(130,88,200,0.55)', flexShrink: 0 }} />
                      {tech}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        background: 'rgba(130,88,200,0.06)', backdropFilter: 'blur(20px)',
        padding: '70px 0', textAlign: 'center',
        borderTop: '1px solid rgba(130,88,200,0.15)',
      }}>
        <div className="container">
          <h2 style={{ color: '#fff', marginBottom: 14, fontSize: 'clamp(1.7rem,4vw,2.3rem)', fontWeight: 700 }}>
            {isAr ? 'لديك مشروع في ذهنك؟' : 'Have a project in mind?'}
          </h2>
          <p style={{ color: '#6b6b88', marginBottom: 28, maxWidth: 440, margin: '0 auto 28px' }}>
            {isAr ? 'دعنا نناقش تحديك ونبني شيئاً رائعاً معاً.' : "Let's discuss your challenge and build something remarkable together."}
          </p>
          <Link to={getPath('/contact')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(102deg, #8258c8, #2c84c8)',
            color: '#fff', padding: '13px 34px', borderRadius: 50,
            textDecoration: 'none', fontWeight: 700, fontSize: 14,
            boxShadow: '0 8px 28px rgba(130,88,200,0.35)',
          }}>
            {isAr ? 'تحدث مع WIZ ←' : 'Talk to WIZ →'}
          </Link>
        </div>
      </section>

      {/* ── Modal ── */}
      {active !== null && (
        <ServiceModal svc={SERVICES[active]} onClose={() => setActive(null)} />
      )}
    </>
  );
};

export default Services;
